/**
 * EstimateMaturityPage — per-estimate maturity surface (v260.117).
 *
 * Mounted at `/estimator/:id/maturity` and rendered inside the same
 * WorkspaceSubNav as Requirements / Project Conditions / Estimate.
 *
 * Behaviour:
 *   - Loads the estimate (for org context + sub-nav state).
 *   - Hits /api/maturity/insights?organization_id={org_id} to fetch the
 *     latest assessment for the customer org.
 *   - When an assessment exists, shows the MaturityGauge + band insights
 *     + primary constraint + Pillar breakdown + a "Re-assess" CTA.
 *   - When no assessment exists, shows an opt-in zero-state with a big
 *     "Take assessment" CTA (the user can also Skip and continue).
 *   - "Apply maturity baseline" button opens the existing
 *     MaturityBaselineDialog so the rep can push the scores into the
 *     estimate's Readiness Factors with a single click.
 *
 * The standalone /maturity dashboard stays in place — this surface is
 * intentionally focused on a single estimate / single customer org.
 */
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import {
  Loader2, ChevronLeft, Gauge, ListChecks, Sparkles, AlertTriangle, TrendingUp,
  ChevronDown, ChevronUp, Download, ExternalLink, RefreshCw,
} from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription,
} from '../components/ui/dialog';
import WorkspaceSubNav, { computeEstimateProgress } from '../components/WorkspaceSubNav';
import MaturityGauge from '../components/MaturityGauge';
import MaturityAssessment from '../components/MaturityAssessment';
import MaturityBaselineDialog from '../components/MaturityBaselineDialog';
import { getMaturityBand } from '../lib/utils';
import { downloadAuthedFile } from '../lib/fileDownload';

const API_URL = process.env.REACT_APP_BACKEND_URL;

function IndicatorPill({ on, label }) {
  return (
    <div
      className={`flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs ${
        on
          ? 'bg-emerald-50 text-emerald-700 border border-emerald-200'
          : 'bg-slate-50 text-slate-500 border border-slate-200'
      }`}
    >
      <span className={`w-1.5 h-1.5 rounded-full ${on ? 'bg-emerald-500' : 'bg-slate-300'}`} />
      {label}
    </div>
  );
}

function PillarRow({ pillar, expanded, onToggle }) {
  const band = getMaturityBand(pillar.score);
  return (
    <div className="border-b border-slate-100 last:border-b-0">
      <button
        type="button"
        onClick={onToggle}
        className="w-full flex items-center gap-3 py-3 px-1 hover:bg-slate-50 transition text-left"
      >
        <div className="w-1 self-stretch rounded-full" style={{ background: band.color }} />
        <div className="flex-1 min-w-0">
          <div className="text-sm font-semibold text-slate-800 truncate">{pillar.name}</div>
          <div className="text-xs text-slate-500">Weight {Math.round(pillar.weight * 100)}%</div>
        </div>
        <div className="flex items-center gap-3">
          <div className="text-right">
            <div className="text-lg font-bold text-[#0F2040] tabular-nums">
              {Math.round(pillar.score)}
            </div>
            <div className="text-[10px] uppercase tracking-wide" style={{ color: band.color }}>
              {band.label}
            </div>
          </div>
          {expanded ? <ChevronUp className="w-4 h-4 text-slate-400" /> : <ChevronDown className="w-4 h-4 text-slate-400" />}
        </div>
      </button>
      {expanded && (
        <div className="bg-slate-50/60 px-4 py-3 space-y-1.5">
          {(pillar.dimensions || []).map((d) => (
            <div key={d.dimension_id} className="flex items-center justify-between text-xs">
              <span className="text-slate-600">{d.name}</span>
              <span className="font-mono text-slate-700">L{d.level} · {Math.round(d.score)}/100</span>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

export default function EstimateMaturityPage() {
  const { id: estimateId } = useParams();
  const navigate = useNavigate();
  const [estimate, setEstimate] = useState(null);
  const [insights, setInsights] = useState(null);
  const [loading, setLoading] = useState(true);
  const [wizardOpen, setWizardOpen] = useState(false);
  const [baselineOpen, setBaselineOpen] = useState(false);
  const [expandedPillar, setExpandedPillar] = useState(null);

  // Load estimate + insights in parallel
  const loadAll = useCallback(async () => {
    setLoading(true);
    try {
      const [estRes] = await Promise.all([
        axios.get(`${API_URL}/api/estimates/${estimateId}`, { withCredentials: true }),
      ]);
      const est = estRes.data || null;
      setEstimate(est);
      const orgId = est?.org_id || null;
      if (orgId) {
        try {
          const r = await axios.get(
            `${API_URL}/api/maturity/insights?organization_id=${encodeURIComponent(orgId)}`,
            { withCredentials: true },
          );
          setInsights(r.data);
        } catch (err) {
          console.warn('maturity insights load failed', err);
          setInsights({ has_assessment: false });
        }
      } else {
        setInsights({ has_assessment: false, missing_org: true });
      }
    } catch (err) {
      console.error('estimate load failed', err);
      toast.error('Could not load this estimate.');
    } finally {
      setLoading(false);
    }
  }, [estimateId]);

  useEffect(() => { loadAll(); }, [loadAll]);

  const progress = useMemo(() => computeEstimateProgress(estimate), [estimate]);
  const orgId = estimate?.org_id || null;
  const orgName = estimate?.organization_name || null;
  const hasData = insights?.has_assessment;
  const band = hasData ? getMaturityBand(insights.overall_score) : null;

  const onWizardComplete = () => {
    setWizardOpen(false);
    loadAll();
  };

  return (
    <div className="min-h-screen bg-slate-50 pb-12" data-testid="estimate-maturity-page">
      <WorkspaceSubNav
        estimateId={estimateId}
        activeTab="maturity"
        hasConditions={!!estimate?.has_conditions}
        onProjectConditionsClick={() =>
          navigate(`/estimator?load=${estimateId}&openConditions=1`)
        }
        customerName={orgName}
        projectName={estimate?.estimate_name}
        progress={progress}
      />

      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-6 space-y-5">
        <div className="flex items-start justify-between gap-4 flex-wrap">
          <div>
            <Button
              variant="ghost"
              size="sm"
              className="text-slate-500 -ml-2 mb-2"
              onClick={() => navigate(`/estimator/${estimateId}/requirements`)}
            >
              <ChevronLeft className="w-4 h-4 mr-1" />
              Back to Requirements
            </Button>
            <h1 className="text-2xl font-bold text-[#0F2040] tracking-tight">
              Organisation Maturity
            </h1>
            <p className="text-sm text-slate-500 mt-1 max-w-2xl">
              Optional but recommended. Scoring {orgName ? <span className="font-semibold text-slate-700">{orgName}</span> : 'this customer'}
              {' '}across 5 pillars / 30 dimensions sets a baseline for the
              Readiness Factors on this estimate and surfaces in the Design Pack.
            </p>
          </div>
          <div className="flex items-center gap-2 flex-wrap">
            <Button
              variant="outline"
              size="icon"
              onClick={loadAll}
              data-testid="estimate-maturity-refresh"
              title="Reload"
            >
              <RefreshCw className="w-4 h-4" />
            </Button>
            {hasData && orgId ? (
              <>
                <Button
                  variant="outline"
                  onClick={async () => {
                    try {
                      await downloadAuthedFile(
                        `/api/maturity/briefing/pdf?organization_id=${encodeURIComponent(orgId)}`,
                        'maturity_briefing.pdf',
                      );
                    } catch (err) {
                      toast.error(err?.response?.data?.detail || 'Could not download the briefing PDF.');
                    }
                  }}
                  data-testid="estimate-maturity-briefing"
                  title="Download a customer-facing one-page briefing PDF"
                >
                  <Download className="w-4 h-4 mr-1.5" />
                  Briefing PDF
                </Button>
                <Button
                  variant="outline"
                  onClick={() => setBaselineOpen(true)}
                  data-testid="estimate-maturity-apply"
                >
                  Apply to Readiness Factors
                </Button>
                <Button
                  variant="outline"
                  onClick={async () => {
                    try {
                      const r = await axios.post(
                        `${API_URL}/api/maturity/apply-readiness-baseline/${estimateId}`,
                        {},
                        { withCredentials: true },
                      );
                      toast.success(`Applied ${r.data?.applied_count || 4} readiness sliders from ${r.data?.maturity_meta?.band} band.`);
                      loadAll();
                    } catch (err) {
                      toast.error(err?.response?.data?.detail || 'Could not apply baseline.');
                    }
                  }}
                  data-testid="estimate-maturity-apply-sliders"
                  title="Push maturity scores into the Project Factors readiness sliders"
                >
                  Apply to Project Factors
                </Button>
              </>
            ) : null}
            <Button
              onClick={() => setWizardOpen(true)}
              className="bg-[#0F2040] hover:bg-[#1a2f5a]"
              data-testid="estimate-maturity-assess"
              disabled={!orgId}
              title={!orgId ? 'Pick a Customer Organisation first' : ''}
            >
              <ListChecks className="w-4 h-4 mr-1.5" />
              {hasData ? 'Re-assess' : 'Take assessment'}
            </Button>
          </div>
        </div>

        {loading ? (
          <div className="flex items-center justify-center py-24">
            <Loader2 className="w-6 h-6 animate-spin text-slate-400" />
          </div>
        ) : !orgId ? (
          <Card className="border-amber-200 bg-amber-50/30">
            <CardContent className="py-10 text-center">
              <AlertTriangle className="w-8 h-8 mx-auto text-amber-500 mb-2" />
              <div className="text-base font-semibold text-amber-900">
                No Customer Organisation linked to this estimate
              </div>
              <p className="text-sm text-amber-800 mt-1 max-w-md mx-auto">
                Maturity assessments are scored per Customer Org. Link this estimate to an
                organisation first, then return here.
              </p>
              <Button
                className="mt-4"
                variant="outline"
                onClick={() => navigate(`/estimator?load=${estimateId}`)}
              >
                Open Estimate · pick a customer
              </Button>
            </CardContent>
          </Card>
        ) : !hasData ? (
          <Card className="border-dashed border-2 border-slate-200" data-testid="estimate-maturity-empty">
            <CardContent className="py-16 text-center">
              <Sparkles className="w-10 h-10 mx-auto text-cyan-400 mb-3" />
              <div className="text-lg font-semibold text-[#0F2040]">
                No assessment yet for {orgName}
              </div>
              <p className="text-sm text-slate-500 max-w-md mx-auto mt-1">
                Score the customer across 30 questions (about 6 minutes) to generate
                the maturity baseline. We'll pre-populate Readiness Factors and
                surface it in the Design Pack.
              </p>
              <div className="mt-5 flex items-center justify-center gap-3">
                <Button
                  onClick={() => setWizardOpen(true)}
                  className="bg-[#0F2040] hover:bg-[#1a2f5a]"
                  data-testid="estimate-maturity-empty-start"
                >
                  Start assessment
                </Button>
                <Button
                  variant="outline"
                  onClick={() => navigate(`/estimator/${estimateId}/requirements`)}
                  data-testid="estimate-maturity-empty-skip"
                >
                  Skip — continue to Requirements
                </Button>
              </div>
              <a
                className="inline-flex items-center gap-1 text-xs text-slate-400 hover:text-slate-600 mt-4"
                href={`/maturity?organization_id=${encodeURIComponent(orgId)}`}
                target="_blank"
                rel="noopener noreferrer"
              >
                or open the full Maturity dashboard <ExternalLink className="w-3 h-3" />
              </a>
            </CardContent>
          </Card>
        ) : (
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-5">
            {/* Gauge + bars */}
            <Card className="lg:col-span-1">
              <CardHeader className="pb-2">
                <CardTitle className="text-sm uppercase tracking-wide text-slate-500">
                  Maturity Score
                </CardTitle>
              </CardHeader>
              <CardContent>
                <MaturityGauge
                  score={insights.overall_score}
                  pillarScores={insights.pillar_scores}
                  pillarTrends={insights.pillar_trends}
                  overallTrend={insights.overall_trend}
                  label={orgName ? `for ${orgName}` : null}
                />
                <p className="text-xs text-slate-500 mt-4 text-center max-w-xs mx-auto">{band?.description}</p>
                {insights.created_at ? (
                  <div className="mt-2 text-xs text-slate-400 text-center">
                    Last assessed {new Date(insights.created_at).toLocaleDateString()}
                  </div>
                ) : null}
              </CardContent>
            </Card>

            {/* Band insights */}
            <Card className="lg:col-span-2">
              <CardHeader className="pb-2">
                <CardTitle className="text-sm uppercase tracking-wide text-slate-500">
                  What this band means
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-3 text-sm">
                  <div>
                    <div className="text-xs uppercase tracking-wide text-slate-400 mb-1">
                      Structural capability
                    </div>
                    <p className="text-slate-700">{insights.band_insights?.structural_capability}</p>
                  </div>
                  <div>
                    <div className="text-xs uppercase tracking-wide text-slate-400 mb-1">
                      Financial realization
                    </div>
                    <p className="text-slate-700">{insights.band_insights?.financial_realization}</p>
                  </div>
                  <div>
                    <div className="text-xs uppercase tracking-wide text-slate-400 mb-1">
                      Risk profile
                    </div>
                    <p className="text-slate-700">{insights.band_insights?.risk_profile}</p>
                  </div>
                  <div>
                    <div className="text-xs uppercase tracking-wide text-slate-400 mb-1">
                      Strategic positioning
                    </div>
                    <p className="text-slate-700">{insights.band_insights?.strategic_positioning}</p>
                  </div>
                </div>

                <div className="flex flex-wrap gap-2 pt-2 border-t border-slate-100">
                  <IndicatorPill on={insights.indicators?.process_maturity} label="Process maturity" />
                  <IndicatorPill on={insights.indicators?.roi_predictability} label="ROI predictability" />
                  <IndicatorPill on={insights.indicators?.executive_visibility} label="Executive visibility" />
                  <IndicatorPill on={insights.indicators?.strategic_integration} label="Strategic integration" />
                </div>

                {insights.uplift?.next_band && (
                  <div className="flex items-center gap-3 bg-cyan-50 border border-cyan-200 rounded-md px-3 py-2 mt-2">
                    <TrendingUp className="w-4 h-4 text-cyan-700" />
                    <div className="text-sm text-cyan-900">
                      Reaching <span className="font-semibold">{insights.uplift.next_band}</span> closes another
                      <span className="font-bold mx-1">{insights.uplift.roi_improvement_pct}</span>
                      percentage points of ROI leakage.
                    </div>
                  </div>
                )}
              </CardContent>
            </Card>

            {/* Applied to Project Factors (v260.120) */}
            {estimate?.modifiers?._maturity_meta?.per_factor ? (
              <Card className="lg:col-span-3 border-cyan-200 bg-cyan-50/30" data-testid="maturity-sliders-panel">
                <CardHeader className="pb-2 flex flex-row items-center gap-2">
                  <TrendingUp className="w-5 h-5 text-cyan-700" />
                  <CardTitle className="text-base text-cyan-900">
                    Applied to Project Factors
                  </CardTitle>
                </CardHeader>
                <CardContent className="space-y-2 text-sm text-cyan-900">
                  {(() => {
                    const m = estimate.modifiers._maturity_meta;
                    const LABELS = {
                      governance: 'Governance Maturity',
                      content_readiness: 'Content Readiness',
                      client_capability: 'Client Team Capability',
                      change_readiness: 'Change Readiness',
                    };
                    const STEP_LABELS = {
                      0.8: 'Very Strong', 0.9: 'Strong', 1.0: 'Neutral',
                      1.2: 'Weak', 1.4: 'Very Weak',
                    };
                    return (
                      <>
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-2 pb-2 border-b border-cyan-100">
                          {Object.entries(m.per_factor || {}).map(([key, info]) => {
                            const liveVal = estimate.modifiers?.[key];
                            const isManual = liveVal !== info.suggested;
                            return (
                              <div key={key} className="flex items-center justify-between gap-2">
                                <span className="text-slate-700">{LABELS[key] || key}</span>
                                <span className="flex items-center gap-2">
                                  <span className="font-mono text-xs">
                                    {STEP_LABELS[liveVal] || liveVal?.toFixed(2)} ({(liveVal ?? 1).toFixed(2)}×)
                                  </span>
                                  {isManual ? (
                                    <span className="px-1.5 py-0.5 rounded text-[10px] font-semibold bg-amber-100 text-amber-800">
                                      MANUAL
                                    </span>
                                  ) : (
                                    <span className="px-1.5 py-0.5 rounded text-[10px] font-semibold bg-emerald-100 text-emerald-800">
                                      AUTO · {m.band}
                                    </span>
                                  )}
                                </span>
                              </div>
                            );
                          })}
                        </div>
                        <div className="text-xs text-cyan-800">
                          <b>Applied by:</b> {m.applied_by_name || '—'} on {m.applied_at ? new Date(m.applied_at).toLocaleString() : '—'}
                          {' · '}
                          <a
                            href={`/estimator?load=${estimateId}#project-factors`}
                            className="underline hover:no-underline"
                          >
                            Open Project Factors
                          </a>
                        </div>
                      </>
                    );
                  })()}
                </CardContent>
              </Card>
            ) : null}

            {/* Primary constraint */}
            {insights.primary_constraint && (
              <Card className="lg:col-span-3 border-amber-200 bg-amber-50/30">
                <CardHeader className="pb-2 flex flex-row items-center gap-2">
                  <AlertTriangle className="w-5 h-5 text-amber-600" />
                  <CardTitle className="text-base text-amber-900">
                    Primary constraint: {insights.primary_constraint.name}
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="text-sm text-amber-900">
                    Scoring <span className="font-bold">{Math.round(insights.primary_constraint.score)}/100</span>
                    &nbsp;— this is the lowest pillar and where investment will compound most.
                  </div>
                  {insights.primary_constraint.symptoms?.length ? (
                    <div className="mt-2">
                      <div className="text-xs uppercase tracking-wide text-amber-700 mb-1">Weak dimensions</div>
                      <ul className="text-xs text-amber-900 list-disc list-inside space-y-0.5">
                        {insights.primary_constraint.symptoms.map((s) => <li key={s}>{s}</li>)}
                      </ul>
                    </div>
                  ) : null}
                </CardContent>
              </Card>
            )}

            {/* Pillar breakdown */}
            <Card className="lg:col-span-3">
              <CardHeader>
                <CardTitle className="text-sm uppercase tracking-wide text-slate-500">
                  Pillar breakdown
                </CardTitle>
              </CardHeader>
              <CardContent className="p-0">
                {(insights.pillar_scores || []).map((p) => (
                  <PillarRow
                    key={p.pillar_id}
                    pillar={p}
                    expanded={expandedPillar === p.pillar_id}
                    onToggle={() => setExpandedPillar(expandedPillar === p.pillar_id ? null : p.pillar_id)}
                  />
                ))}
              </CardContent>
            </Card>
          </div>
        )}
      </div>

      {/* Assessment wizard */}
      <Dialog open={wizardOpen} onOpenChange={setWizardOpen}>
        <DialogContent className="max-w-3xl max-h-[92vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>
              {orgName ? `Maturity assessment · ${orgName}` : 'Maturity assessment'}
            </DialogTitle>
            <DialogDescription>
              Pick the statement that best describes the customer today.
              Answers persist locally until you submit.
            </DialogDescription>
          </DialogHeader>
          <MaturityAssessment
            organizationId={orgId}
            organizationName={orgName}
            onComplete={onWizardComplete}
            onCancel={() => setWizardOpen(false)}
          />
        </DialogContent>
      </Dialog>

      {/* Apply to Readiness Factors */}
      <MaturityBaselineDialog
        open={baselineOpen}
        onOpenChange={setBaselineOpen}
        organizationId={orgId}
        organizationName={orgName}
        currentMaturity={estimate?.requirements_doc?.maturity_assessment}
        onApply={async (rows) => {
          // Persist the merged readiness rows back to the estimate's
          // requirements_doc.maturity_assessment section so they show up
          // when the rep opens the Requirements form.
          try {
            const rd = estimate?.requirements_doc || {};
            const cur = rd.maturity_assessment || [];
            const byKey = new Map(cur.map((m) => [m.factor_key, m]));
            rows.forEach((r) => {
              const prev = byKey.get(r.factor_key) || {
                factor_key: r.factor_key, level: '', notes: '',
              };
              byKey.set(r.factor_key, {
                ...prev, level: r.level, notes: r.notes || prev.notes,
              });
            });
            const next = { ...rd, maturity_assessment: Array.from(byKey.values()) };
            await axios.put(
              `${API_URL}/api/estimates/${estimateId}/requirements`,
              { requirements_doc: next },
              { withCredentials: true },
            );
            toast.success('Readiness Factors updated from maturity baseline.');
            loadAll();
          } catch (err) {
            console.error('apply baseline failed', err);
            toast.error('Could not save the baseline to this estimate.');
          }
        }}
      />
    </div>
  );
}
